import { z } from 'zod';

/**
 * Client-side environment variables schema
 * Only VITE_ prefixed variables are exposed to the client
 */
const clientEnvSchema = z.object({
  VITE_APP_URL: z.string().url().default('http://localhost:5173'),
  VITE_GA_MEASUREMENT_ID: z.string().optional(),
  VITE_CALCOM_USERNAME: z.string().optional(),
});

/**
 * Server-side environment variables schema
 * These are only available in the server context
 */
export const serverEnvSchema = z.object({
  NODE_ENV: z.enum(['development', 'production', 'test']).default('development'),
  PORT: z.string().transform(Number).default('3001'),
  API_URL: z.string().url().default('http://localhost:3001'),

  // Database
  DATABASE_URL: z.string().min(1, 'DATABASE_URL is required'),

  // Authentication
  JWT_SECRET: z.string().min(32, 'JWT_SECRET must be at least 32 characters'),
  JWT_EXPIRES_IN: z.string().default('24h'),
  SESSION_SECRET: z.string().min(32, 'SESSION_SECRET must be at least 32 characters'),

  // Email - ConvertKit
  CONVERTKIT_API_KEY: z.string().optional(),
  CONVERTKIT_API_SECRET: z.string().optional(),
  CONVERTKIT_FORM_ID: z.string().optional(),

  // Calendar - Cal.com
  CALCOM_API_KEY: z.string().optional(),
  CALCOM_EVENT_TYPE_ID: z.string().optional(),

  // Rate Limiting
  RATE_LIMIT_WINDOW_MS: z.string().transform(Number).default('900000'),
  RATE_LIMIT_MAX_REQUESTS: z.string().transform(Number).default('100'),

  // CORS
  CORS_ORIGIN: z.string().default('http://localhost:5173'),

  // Feature Flags
  ENABLE_ANALYTICS: z
    .string()
    .transform((v) => v === 'true')
    .default('true'),
  ENABLE_NEWSLETTER: z
    .string()
    .transform((v) => v === 'true')
    .default('true'),
  ENABLE_BOOKING: z
    .string()
    .transform((v) => v === 'true')
    .default('true'),
});

/**
 * Type definitions for environment variables
 */
export type ClientEnv = z.infer<typeof clientEnvSchema>;
export type ServerEnv = z.infer<typeof serverEnvSchema>;

/**
 * Validates and returns client-side environment variables
 * Safe to use in browser context
 */
export function getClientEnv(): ClientEnv {
  const parsed = clientEnvSchema.safeParse({
    VITE_APP_URL: import.meta.env['VITE_APP_URL'],
    VITE_GA_MEASUREMENT_ID: import.meta.env['VITE_GA_MEASUREMENT_ID'],
    VITE_CALCOM_USERNAME: import.meta.env['VITE_CALCOM_USERNAME'],
  });

  if (!parsed.success) {
    console.error('Invalid client environment variables:', parsed.error.flatten().fieldErrors);
    throw new Error('Invalid client environment variables');
  }

  return parsed.data;
}

/**
 * Validates and returns server-side environment variables
 * Only use in server context (server/ directory)
 */
export function getServerEnv(): ServerEnv {
  const parsed = serverEnvSchema.safeParse(process.env);

  if (!parsed.success) {
    console.error('Invalid server environment variables:', parsed.error.flatten().fieldErrors);
    throw new Error('Invalid server environment variables');
  }

  return parsed.data;
}

/**
 * Helper to check if we're in development mode
 */
export function isDevelopment(): boolean {
  return import.meta.env['DEV'] === true;
}

/**
 * Helper to check if we're in production mode
 */
export function isProduction(): boolean {
  return import.meta.env['PROD'] === true;
}

/**
 * Client environment singleton (lazy initialized)
 */
let clientEnvInstance: ClientEnv | null = null;

export function env(): ClientEnv {
  if (!clientEnvInstance) {
    clientEnvInstance = getClientEnv();
  }
  return clientEnvInstance;
}
